import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_STRING as api } from '@/App';
import { Package } from 'lucide-react';
import { toast } from 'sonner';

import { logger } from '@/utils/logger';

import { ArrowLeft, Calendar, Heart, Loader2 } from 'lucide-react';
import ProductCard from '@/components/ProductCard';
import { Button } from '@/components/ui/button';
import EmptyState from '@/components/EmptyState';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

export default function SharedRecipient() {
  const { shareToken } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dynamic OG tags per shared recipient, see useDocumentMeta docs.
  const recipientName = data?.recipient?.name || 'Someone';
  useDocumentMeta({
    title: data?.recipient
      ? `Gift ideas for ${recipientName}, Mementa`
      : 'Shared Keepsake, Mementa',
    description: data?.recipient
      ? `See gift ideas ${recipientName} would love, shared on Mementa.`
      : 'View a keepsake profile shared with you on Mementa.',
    type: 'profile',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${api}/shared/recipient/${shareToken}`);
        if (!res.ok) {
          if (cancelled) return;
          setError(res.status === 404 ? 'Profile not found or no longer shared' : 'Failed to load profile');
          setLoading(false);
          return;
        }
        const json = await res.json();
        if (cancelled) return;
        setData(json);
      } catch (err) {
        if (cancelled) return;
        logger.error('Error fetching shared recipient:', err);
        setError('Failed to load profile');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [shareToken]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: '#9E6752' }} />
          <p className="text-gray-600">Loading profile…</p>
        </div>
      </div>
    );
  }

  if (error || !data?.recipient) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <Heart className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h1 className="text-2xl font-bold mb-2" style={{ color: '#20212B' }}>Profile Unavailable</h1>
          <p className="text-gray-600 mb-6">{error || 'This profile is private or no longer shared.'}</p>
          <Button
            onClick={() => navigate('/')}
            style={{ background: '#9E6752', color: 'white' }}
            data-testid="shared-recipient-home-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Browse Mementa
          </Button>
        </div>
      </div>
    );
  }

  const { recipient, gifts } = data;
  const occasions = recipient.occasions || [];
  const interests = recipient.interests || [];
  const cardColor = recipient.color || '#9E6752';

  return (
    <div className="min-h-screen bg-gray-50 page-enter">
      {/* Header banner, painted with the recipient's profile color */}
      <div style={{ background: cardColor }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-sm mb-6 hover:underline"
            style={{ color: 'rgba(255,255,255,0.9)' }}
            data-testid="shared-recipient-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Browse All Products
          </button>

          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.9)' }}>
              <Heart className="w-7 h-7" style={{ color: cardColor }} />
            </div>
            <div className="flex-1 min-w-0">
              <h1
                className="text-3xl sm:text-4xl font-bold leading-tight"
                style={{ color: 'white', fontFamily: 'Playfair Display, serif' }}
                data-testid="shared-recipient-name"
              >
                {recipient.name}
              </h1>
              {recipient.relationship && (
                <p className="text-sm opacity-90 mt-1" style={{ color: 'white' }}>
                  {recipient.relationship}
                </p>
              )}
              {occasions.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  {occasions.map((occ, idx) => {
                    let dateLabel = '';
                    if (occ.date) {
                      const d = new Date(occ.date);
                      if (!isNaN(d.getTime())) {
                        dateLabel = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      }
                    }
                    return (
                      <span
                        key={`${occ.name}-${idx}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium leading-none whitespace-nowrap"
                        style={{ background: 'rgba(255,255,255,0.22)', color: 'white' }}
                      >
                        <Calendar className="w-3.5 h-3.5 opacity-90" />
                        <span>{occ.name}</span>
                        {dateLabel && <span className="opacity-80 font-normal">· {dateLabel}</span>}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Interests + notes */}
        {(interests.length > 0 || (recipient.notes && recipient.notes.trim())) && (
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 space-y-4">
            {interests.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold uppercase tracking-wide mb-2" style={{ color: '#534145' }}>Interests</h2>
                <div className="flex flex-wrap gap-2">
                  {interests.map((interest) => (
                    <span
                      key={interest}
                      className="text-xs px-2.5 py-1 rounded-full font-medium"
                      style={{ background: `${cardColor}20`, color: '#534145' }}
                    >
                      {interest}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {recipient.notes && recipient.notes.trim() && (
              <div>
                <h2 className="text-sm font-semibold uppercase tracking-wide mb-2" style={{ color: '#534145' }}>Notes</h2>
                <p className="text-sm leading-relaxed" style={{ color: '#20212B', whiteSpace: 'pre-wrap' }}>
                  {recipient.notes}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Saved gifts */}
        <div>
          <h2
            className="text-2xl font-bold mb-4"
            style={{ color: '#20212B', fontFamily: 'Playfair Display, serif' }}
          >
            Saved Gift Ideas
            <span className="ml-2 text-base font-normal text-gray-500">
              ({gifts?.length || 0})
            </span>
          </h2>

          {!gifts || gifts.length === 0 ? (
            <EmptyState
              icon={Package}
              title="No gift ideas saved yet"
              description="The owner is still curating their list, check back soon."
              compact
              testId="shared-recipient-empty"
            />
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
              {gifts.map((g) => (
                <ProductCard
                  key={g.product?.id || g.id}
                  product={g.product}
                  onQuickView={() => {}}
                  isInWishlist={false}
                  hideHeartButton={true}
                  onWishlistToggle={() => toast.info('Create an account to save items')}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* CTA Footer */}
      <div className="bg-white border-t mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <h3 className="text-2xl font-bold mb-3" style={{ color: '#20212B' }}>
            Find the perfect gift for someone you love
          </h3>
          <p className="text-gray-600 mb-6">
            Mementa helps you save and share gift ideas for everyone in your life.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => navigate('/signup')} style={{ background: '#2D4354', color: 'white' }}>
              Sign Up Free
            </Button>
            <Button onClick={() => navigate('/')} variant="outline" style={{ borderColor: '#9E6752', color: '#9E6752' }}>
              Browse Products
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
